/**
 * Pre-built Deal Flow Templates
 * Users can start with these instead of building from scratch
 */

export const FLOW_TEMPLATES = [
    {
        id: 'blank',
        name: 'Blank Flow',
        description: 'Start from scratch with just a start node',
        icon: 'Plus',
        color: 'slate',
        nodes: [
            {
                id: 'start_1',
                type: 'start',
                position: { x: 100, y: 200 },
                data: { title: 'Start', trigger: 'conversation_start' },
            }
        ],
        connections: [],
    },
    {
        id: 'quick-3-step',
        name: 'Quick Close (3 Steps)',
        description: 'Greet → Ask interest → Close the deal',
        icon: 'Zap',
        color: 'emerald',
        nodes: [
            {
                id: 'start_1',
                type: 'start',
                position: { x: 50, y: 150 },
                data: { title: 'Customer Messages', trigger: 'conversation_start' },
            },
            {
                id: 'message_1',
                type: 'message',
                position: { x: 370, y: 100 },
                data: {
                    title: 'Greeting',
                    message: 'Hi {name}! 👋 Thanks for reaching out. I\'d love to help you today. What product are you interested in?'
                },
            },
            {
                id: 'question_1',
                type: 'question',
                position: { x: 690, y: 100 },
                data: {
                    title: 'Confirm Interest',
                    message: 'Great choice! Would you like me to help you place an order right now? I can walk you through it.',
                    responseType: 'yes_no',
                    waitForResponse: true,
                },
            },
            {
                id: 'condition_1',
                type: 'condition',
                position: { x: 1010, y: 100 },
                data: {
                    title: 'Ready to Buy?',
                    conditionType: 'response',
                    branches: [
                        { label: 'Yes', condition: 'contains "yes" or "sure" or "ok"' },
                        { label: 'No', condition: '' },
                    ],
                },
            },
            {
                id: 'order_1',
                type: 'order',
                position: { x: 1330, y: 50 },
                data: {
                    title: 'Create Order',
                    collectFields: ['name', 'phone', 'address'],
                    successMessage: '🎉 Order confirmed! We\'ll contact you shortly with delivery details.',
                },
            },
            {
                id: 'message_2',
                type: 'message',
                position: { x: 1330, y: 220 },
                data: {
                    title: 'Follow Up Later',
                    message: 'No problem! Take your time. Feel free to message us whenever you\'re ready. Is there anything else I can help you with?'
                },
            },
            {
                id: 'success_1',
                type: 'success',
                position: { x: 1650, y: 50 },
                data: { title: 'Deal Closed', message: 'Customer converted!' },
            },
            {
                id: 'failure_1',
                type: 'failure',
                position: { x: 1650, y: 220 },
                data: { title: 'Not Ready', message: 'Customer needs more time' },
            },
        ],
        connections: [
            { id: 'conn_1', from: 'start_1', fromOutput: 0, to: 'message_1' },
            { id: 'conn_2', from: 'message_1', fromOutput: 0, to: 'question_1' },
            { id: 'conn_3', from: 'question_1', fromOutput: 0, to: 'condition_1' },
            { id: 'conn_4', from: 'condition_1', fromOutput: 0, to: 'order_1' },
            { id: 'conn_5', from: 'condition_1', fromOutput: 1, to: 'message_2' },
            { id: 'conn_6', from: 'order_1', fromOutput: 0, to: 'success_1' },
            { id: 'conn_7', from: 'message_2', fromOutput: 0, to: 'failure_1' },
        ],
    },
    {
        id: 'objection-handling',
        name: 'Handle Objections (5 Steps)',
        description: 'Greet → Present → Handle price/timing objections → Close',
        icon: 'Shield',
        color: 'amber',
        nodes: [
            {
                id: 'start_1',
                type: 'start',
                position: { x: 50, y: 200 },
                data: { title: 'Customer Inquiry', trigger: 'conversation_start' },
            },
            {
                id: 'message_1',
                type: 'message',
                position: { x: 370, y: 200 },
                data: {
                    title: 'Welcome & Qualify',
                    message: 'Hi {name}! Thanks for your interest. I\'m here to help you find the perfect solution. Can you tell me a bit about what you\'re looking for?'
                },
            },
            {
                id: 'question_1',
                type: 'question',
                position: { x: 690, y: 200 },
                data: {
                    title: 'Present Solution',
                    message: 'Based on what you\'ve shared, I think our {product} would be perfect for you! It\'s priced at {price}. Would you like to proceed with the order?',
                    responseType: 'any',
                    waitForResponse: true,
                },
            },
            {
                id: 'condition_1',
                type: 'condition',
                position: { x: 1010, y: 200 },
                data: {
                    title: 'Check Response',
                    conditionType: 'response',
                    branches: [
                        { label: 'Ready to Buy', condition: 'contains "yes" or "order" or "buy"' },
                        { label: 'Price Concern', condition: 'contains "expensive" or "price" or "cost" or "cheaper"' },
                        { label: 'Need Time', condition: 'contains "later" or "think" or "not sure"' },
                    ],
                },
            },
            // Price Objection Branch
            {
                id: 'message_price',
                type: 'message',
                position: { x: 1330, y: 150 },
                data: {
                    title: 'Address Price',
                    message: 'I totally understand budget is important! Let me share why this is actually great value:\n\n✅ Premium quality that lasts\n✅ Free shipping included\n✅ 30-day money-back guarantee\n\nWould a 10% first-order discount help? Use code WELCOME10!'
                },
            },
            {
                id: 'question_price',
                type: 'question',
                position: { x: 1650, y: 150 },
                data: {
                    title: 'Discount Offer',
                    message: 'With the discount, your total would be {discounted_price}. Ready to give it a try?',
                    responseType: 'yes_no',
                    waitForResponse: true,
                },
            },
            // Timing Objection Branch
            {
                id: 'message_timing',
                type: 'message',
                position: { x: 1330, y: 350 },
                data: {
                    title: 'Create Urgency',
                    message: 'I completely understand! Just so you know:\n\n⏰ This price is only valid today\n🔥 We only have limited stock left\n🚚 Order now for fastest delivery\n\nWould you like me to reserve one for you?'
                },
            },
            {
                id: 'question_timing',
                type: 'question',
                position: { x: 1650, y: 350 },
                data: {
                    title: 'Reserve Item',
                    message: 'I can hold this item for 2 hours while you decide. Should I reserve it?',
                    responseType: 'yes_no',
                    waitForResponse: true,
                },
            },
            // Order Flow
            {
                id: 'order_1',
                type: 'order',
                position: { x: 1330, y: 0 },
                data: {
                    title: 'Process Order',
                    collectFields: ['name', 'phone', 'address'],
                    successMessage: '🎉 Awesome! Your order is confirmed. You\'ll receive a confirmation message shortly!'
                },
            },
            // Final Outcomes
            {
                id: 'condition_final',
                type: 'condition',
                position: { x: 1970, y: 250 },
                data: {
                    title: 'Final Decision',
                    conditionType: 'response',
                    branches: [
                        { label: 'Yes', condition: 'contains "yes" or "ok" or "sure"' },
                        { label: 'No', condition: '' },
                    ],
                },
            },
            {
                id: 'success_1',
                type: 'success',
                position: { x: 1650, y: 0 },
                data: { title: 'Sale Complete!', message: 'Customer purchased' },
            },
            {
                id: 'message_nurture',
                type: 'message',
                position: { x: 2290, y: 300 },
                data: {
                    title: 'Nurture Lead',
                    message: 'No worries at all! I\'ll send you more info via message. Feel free to reach out anytime - I\'m here to help! 😊'
                },
            },
            {
                id: 'action_tag',
                type: 'action',
                position: { x: 2610, y: 300 },
                data: {
                    title: 'Tag as Lead',
                    actionType: 'add_tag',
                    tagName: 'warm-lead'
                },
            },
            {
                id: 'failure_1',
                type: 'failure',
                position: { x: 2930, y: 300 },
                data: { title: 'Nurturing', message: 'Added to follow-up list' },
            },
        ],
        connections: [
            { id: 'conn_1', from: 'start_1', fromOutput: 0, to: 'message_1' },
            { id: 'conn_2', from: 'message_1', fromOutput: 0, to: 'question_1' },
            { id: 'conn_3', from: 'question_1', fromOutput: 0, to: 'condition_1' },
            // Ready to buy
            { id: 'conn_4', from: 'condition_1', fromOutput: 0, to: 'order_1' },
            { id: 'conn_5', from: 'order_1', fromOutput: 0, to: 'success_1' },
            // Price objection
            { id: 'conn_6', from: 'condition_1', fromOutput: 1, to: 'message_price' },
            { id: 'conn_7', from: 'message_price', fromOutput: 0, to: 'question_price' },
            { id: 'conn_8', from: 'question_price', fromOutput: 0, to: 'condition_final' },
            // Timing objection
            { id: 'conn_9', from: 'condition_1', fromOutput: 2, to: 'message_timing' },
            { id: 'conn_10', from: 'message_timing', fromOutput: 0, to: 'question_timing' },
            { id: 'conn_11', from: 'question_timing', fromOutput: 0, to: 'condition_final' },
            // Final decision
            { id: 'conn_12', from: 'condition_final', fromOutput: 0, to: 'order_1' },
            { id: 'conn_13', from: 'condition_final', fromOutput: 1, to: 'message_nurture' },
            { id: 'conn_14', from: 'message_nurture', fromOutput: 0, to: 'action_tag' },
            { id: 'conn_15', from: 'action_tag', fromOutput: 0, to: 'failure_1' },
        ],
    },
    {
        id: 'product-recommendation',
        name: 'Product Recommendation',
        description: 'Ask questions → Recommend product → Upsell → Close',
        icon: 'Package',
        color: 'violet',
        nodes: [
            {
                id: 'start_1',
                type: 'start',
                position: { x: 50, y: 200 },
                data: { title: 'Start Chat', trigger: 'conversation_start' },
            },
            {
                id: 'message_1',
                type: 'message',
                position: { x: 370, y: 200 },
                data: {
                    title: 'Welcome',
                    message: 'Welcome! 🛍️ I\'m your personal shopping assistant. Let me help you find the perfect product!\n\nWhat are you shopping for today?'
                },
            },
            {
                id: 'question_1',
                type: 'question',
                position: { x: 690, y: 200 },
                data: {
                    title: 'Understand Needs',
                    message: 'Great! To recommend the best option, could you tell me:\n\n1️⃣ What\'s your budget range?\n2️⃣ Any specific features you need?',
                    responseType: 'any',
                    waitForResponse: true,
                },
            },
            {
                id: 'message_2',
                type: 'message',
                position: { x: 1010, y: 200 },
                data: {
                    title: 'Recommendation',
                    message: 'Based on your needs, I\'d recommend our {product}! 🌟\n\n✅ {feature_1}\n✅ {feature_2}\n✅ {feature_3}\n\nPrice: {price}\n\nWould you like to add this to your cart?'
                },
            },
            {
                id: 'condition_1',
                type: 'condition',
                position: { x: 1330, y: 200 },
                data: {
                    title: 'Interest Level',
                    conditionType: 'response',
                    branches: [
                        { label: 'Add to Cart', condition: 'contains "yes" or "add" or "cart"' },
                        { label: 'Show More', condition: 'contains "other" or "more" or "different"' },
                        { label: 'Not Interested', condition: '' },
                    ],
                },
            },
            {
                id: 'message_upsell',
                type: 'message',
                position: { x: 1650, y: 100 },
                data: {
                    title: 'Upsell',
                    message: 'Excellent choice! 🎉\n\nMany customers also love pairing this with our {accessory} - it\'s 20% off when bundled!\n\nWould you like to add it?'
                },
            },
            {
                id: 'order_1',
                type: 'order',
                position: { x: 1970, y: 100 },
                data: {
                    title: 'Checkout',
                    collectFields: ['name', 'phone', 'address'],
                    successMessage: '🛒 Order placed! Thank you for shopping with us!'
                },
            },
            {
                id: 'message_alternatives',
                type: 'message',
                position: { x: 1650, y: 250 },
                data: {
                    title: 'Show Alternatives',
                    message: 'Of course! Here are some other great options:\n\n1️⃣ {alt_product_1} - {alt_price_1}\n2️⃣ {alt_product_2} - {alt_price_2}\n3️⃣ {alt_product_3} - {alt_price_3}\n\nWhich one catches your eye?'
                },
            },
            {
                id: 'success_1',
                type: 'success',
                position: { x: 2290, y: 100 },
                data: { title: 'Purchase Complete!', message: 'Order confirmed' },
            },
            {
                id: 'message_save',
                type: 'message',
                position: { x: 1650, y: 400 },
                data: {
                    title: 'Save for Later',
                    message: 'No problem! I\'ve saved these recommendations for you. Come back anytime - I\'ll be here! 👋'
                },
            },
            {
                id: 'failure_1',
                type: 'failure',
                position: { x: 1970, y: 400 },
                data: { title: 'Browsing Only', message: 'No purchase this time' },
            },
        ],
        connections: [
            { id: 'conn_1', from: 'start_1', fromOutput: 0, to: 'message_1' },
            { id: 'conn_2', from: 'message_1', fromOutput: 0, to: 'question_1' },
            { id: 'conn_3', from: 'question_1', fromOutput: 0, to: 'message_2' },
            { id: 'conn_4', from: 'message_2', fromOutput: 0, to: 'condition_1' },
            { id: 'conn_5', from: 'condition_1', fromOutput: 0, to: 'message_upsell' },
            { id: 'conn_6', from: 'condition_1', fromOutput: 1, to: 'message_alternatives' },
            { id: 'conn_7', from: 'condition_1', fromOutput: 2, to: 'message_save' },
            { id: 'conn_8', from: 'message_upsell', fromOutput: 0, to: 'order_1' },
            { id: 'conn_9', from: 'message_alternatives', fromOutput: 0, to: 'condition_1' },
            { id: 'conn_10', from: 'order_1', fromOutput: 0, to: 'success_1' },
            { id: 'conn_11', from: 'message_save', fromOutput: 0, to: 'failure_1' },
        ],
    },
    {
        id: 'appointment-booking',
        name: 'Appointment Booking',
        description: 'Qualify → Check availability → Book appointment',
        icon: 'Calendar',
        color: 'sky',
        nodes: [
            {
                id: 'start_1',
                type: 'start',
                position: { x: 50, y: 200 },
                data: { title: 'Booking Request', trigger: 'keyword', keywords: 'book, appointment, schedule, meeting' },
            },
            {
                id: 'message_1',
                type: 'message',
                position: { x: 370, y: 200 },
                data: {
                    title: 'Greeting',
                    message: 'Hi {name}! I\'d be happy to help you book an appointment. 📅\n\nWhat service are you interested in?'
                },
            },
            {
                id: 'question_1',
                type: 'question',
                position: { x: 690, y: 200 },
                data: {
                    title: 'Select Date',
                    message: 'Perfect! When would you like to come in?\n\n📆 Available slots this week:\n• Monday 2pm, 4pm\n• Tuesday 10am, 3pm\n• Wednesday 11am, 2pm\n\nWhich works best for you?',
                    responseType: 'any',
                    waitForResponse: true,
                },
            },
            {
                id: 'message_2',
                type: 'message',
                position: { x: 1010, y: 200 },
                data: {
                    title: 'Confirm Details',
                    message: 'Great! Let me confirm your appointment:\n\n📅 {selected_date}\n⏰ {selected_time}\n💼 {service}\n\nI\'ll need a few details to complete the booking.'
                },
            },
            {
                id: 'order_1',
                type: 'order',
                position: { x: 1330, y: 200 },
                data: {
                    title: 'Collect Info',
                    collectFields: ['name', 'phone', 'email'],
                    successMessage: '✅ Your appointment is confirmed! You\'ll receive a reminder before your visit.'
                },
            },
            {
                id: 'message_reminder',
                type: 'message',
                position: { x: 1650, y: 150 },
                data: {
                    title: 'Confirmation',
                    message: '🎉 All set! Here\'s your confirmation:\n\n📅 {date} at {time}\n📍 {location}\n\nWe\'ll send you a reminder 24 hours before. See you soon!'
                },
            },
            {
                id: 'success_1',
                type: 'success',
                position: { x: 1970, y: 150 },
                data: { title: 'Booked!', message: 'Appointment scheduled' },
            },
            {
                id: 'message_failed',
                type: 'message',
                position: { x: 1650, y: 300 },
                data: {
                    title: 'Reschedule',
                    message: 'No worries! Would you like to try a different time, or should I have someone call you to help schedule?'
                },
            },
            {
                id: 'failure_1',
                type: 'failure',
                position: { x: 1970, y: 300 },
                data: { title: 'Need Follow-up', message: 'Requires manual scheduling' },
            },
        ],
        connections: [
            { id: 'conn_1', from: 'start_1', fromOutput: 0, to: 'message_1' },
            { id: 'conn_2', from: 'message_1', fromOutput: 0, to: 'question_1' },
            { id: 'conn_3', from: 'question_1', fromOutput: 0, to: 'message_2' },
            { id: 'conn_4', from: 'message_2', fromOutput: 0, to: 'order_1' },
            { id: 'conn_5', from: 'order_1', fromOutput: 0, to: 'message_reminder' },
            { id: 'conn_6', from: 'order_1', fromOutput: 1, to: 'message_failed' },
            { id: 'conn_7', from: 'message_reminder', fromOutput: 0, to: 'success_1' },
            { id: 'conn_8', from: 'message_failed', fromOutput: 0, to: 'failure_1' },
        ],
    },
];

export default FLOW_TEMPLATES;
